import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Gift, Plus, Copy, Send, XCircle, Search, Pencil, Save as SaveIcon, X as XIcon, Sparkles, Tag, Percent } from "lucide-react";
import { safeFormat } from "@/lib/dateUtils";

const PRESET_AMOUNTS = [10, 20, 30, 50, 100];

// Helper: default expiry date = 3 months from today
const defaultExpiry = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 3);
  return d.toISOString().split('T')[0];
};

const emptyPromoForm = {
  code: '', description: '', discount_type: 'percent', discount_percent: 10, discount_amount: 10,
  start_date: '', end_date: '', min_spend: '', max_redemptions: '',
};

const promoStatusColor = {
  ACTIVE: 'bg-green-100 text-green-700', SCHEDULED: 'bg-blue-100 text-blue-700',
  EXPIRED: 'bg-yellow-100 text-yellow-700', DEPLETED: 'bg-gray-100 text-gray-500',
  CANCELLED: 'bg-red-100 text-red-700',
};

export default function Vouchers() {
  const [tab, setTab] = useState('vouchers');
  const [vouchers, setVouchers] = useState([]);
  const [stats, setStats] = useState({});
  const [dialog, setDialog] = useState(null);
  const [detailDialog, setDetailDialog] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [form, setForm] = useState({
    type: 'GIFT_CARD', initial_value: 50, issued_to_name: '', issued_to_email: '',
    recipient_name: '', recipient_email: '', recipient_message: '',
    issued_reason: '', expiry_date: defaultExpiry(), min_spend: ''
  });

  // ── Promo codes state ──
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoStats, setPromoStats] = useState({});
  const [promoSearch, setPromoSearch] = useState('');
  const [promoFilterStatus, setPromoFilterStatus] = useState('all');
  const [promoDialog, setPromoDialog] = useState(false);
  const [promoForm, setPromoForm] = useState(emptyPromoForm);
  const [promoDetailDialog, setPromoDetailDialog] = useState(false);
  const [promoDetailData, setPromoDetailData] = useState(null);
  const [promoEditMode, setPromoEditMode] = useState(false);
  const [promoEditForm, setPromoEditForm] = useState({});
  const [promoSavingEdit, setPromoSavingEdit] = useState(false);

  const load = async () => {
    try {
      const [vRes, sRes] = await Promise.all([
        axios.get(`${API}/vouchers${filterStatus !== 'all' ? '?status=' + filterStatus : ''}`),
        axios.get(`${API}/vouchers/stats`)
      ]);
      setVouchers(vRes.data.vouchers || []);
      setStats(sRes.data);
    } catch (e) {  }
  };

  const loadPromoCodes = async () => {
    try {
      const [pRes, sRes] = await Promise.all([
        axios.get(`${API}/promo-codes`),
        axios.get(`${API}/promo-codes/stats`),
      ]);
      setPromoCodes(pRes.data.promo_codes || []);
      setPromoStats(sRes.data);
    } catch (e) {  }
  };

  useEffect(() => { load(); }, [filterStatus]);
  useEffect(() => { loadPromoCodes(); }, []);

  const save = async () => {
    try {
      const payload = { ...form };
      if (payload.min_spend) payload.min_spend = parseFloat(payload.min_spend);
      await axios.post(`${API}/vouchers`, payload);
      toast.success('Voucher created & email sent!');
      setDialog(null);
      load();
    } catch (e) { toast.error('Failed to create'); }
  };

  const viewDetail = async (v) => {
    try {
      const res = await axios.get(`${API}/vouchers/${v.id}`);
      setDetailData(res.data);
      setEditMode(false);
      setDetailDialog(true);
    } catch (e) { toast.error('Failed to load'); }
  };

  const startEdit = () => {
    if (!detailData?.voucher) return;
    const v = detailData.voucher;
    setEditForm({
      initial_value: v.initial_value ?? 0,
      recipient_name: v.recipient_name || '',
      recipient_email: v.recipient_email || '',
      recipient_message: v.recipient_message || '',
      issued_to_name: v.issued_to_name || '',
      issued_to_email: v.issued_to_email || '',
      purchased_by_name: v.purchased_by_name || '',
      purchased_by_email: v.purchased_by_email || '',
      issued_reason: v.issued_reason || '',
      expiry_date: v.expiry_date || '',
      min_spend: v.min_spend ?? '',
      discount_percent: v.discount_percent ?? 0,
    });
    setEditMode(true);
  };

  const saveEdit = async () => {
    if (!detailData?.voucher) return;
    setSavingEdit(true);
    try {
      const payload = { ...editForm };
      payload.initial_value = parseFloat(payload.initial_value) || 0;
      if (payload.min_spend === '' || payload.min_spend === null) {
        payload.min_spend = null;
      } else {
        payload.min_spend = parseFloat(payload.min_spend);
      }
      if (detailData.voucher.discount_type !== 'percent') {
        delete payload.discount_percent;
      } else {
        payload.discount_percent = parseFloat(payload.discount_percent) || 0;
      }
      const res = await axios.put(`${API}/vouchers/${detailData.voucher.id}`, payload);
      if (res.data?.success) {
        toast.success('Voucher updated');
        // Refresh detail + list
        const fresh = await axios.get(`${API}/vouchers/${detailData.voucher.id}`);
        setDetailData(fresh.data);
        setEditMode(false);
        load();
      } else {
        toast.error(res.data?.error || 'Failed to update');
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to update');
    }
    setSavingEdit(false);
  };

  const cancelVoucher = async (id) => {
    if (!window.confirm('Cancel this voucher?')) return;
    await axios.delete(`${API}/vouchers/${id}`);
    toast.success('Voucher cancelled');
    load();
    setDetailDialog(false);
  };

  const resendEmail = async (id) => {
    const res = await axios.post(`${API}/vouchers/${id}/resend`);
    if (res.data.success) toast.success('Email resent');
    else toast.error(res.data.error || 'Failed');
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied!');
  };

  // ── Promo code actions ──
  const savePromo = async () => {
    try {
      const payload = { ...promoForm, code: (promoForm.code || '').toUpperCase().trim() };
      if (payload.min_spend === '') delete payload.min_spend;
      if (payload.max_redemptions === '') delete payload.max_redemptions;
      if (!payload.start_date) delete payload.start_date;
      if (!payload.end_date) delete payload.end_date;
      const res = await axios.post(`${API}/promo-codes`, payload);
      if (res.data?.error) {
        toast.error(res.data.error);
        return;
      }
      toast.success('Promo code created!');
      setPromoDialog(false);
      setPromoForm(emptyPromoForm);
      loadPromoCodes();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to create promo code');
    }
  };

  const viewPromoDetail = async (p) => {
    try {
      const res = await axios.get(`${API}/promo-codes/${p.id}`);
      setPromoDetailData(res.data);
      setPromoEditMode(false);
      setPromoDetailDialog(true);
    } catch (e) { toast.error('Failed to load'); }
  };

  const startPromoEdit = () => {
    if (!promoDetailData?.promo_code) return;
    const p = promoDetailData.promo_code;
    setPromoEditForm({
      description: p.description || '',
      start_date: p.start_date || '',
      end_date: p.end_date || '',
      min_spend: p.min_spend ?? '',
      max_redemptions: p.max_redemptions ?? '',
      discount_percent: p.discount_percent ?? 0,
      discount_amount: p.discount_amount ?? 0,
    });
    setPromoEditMode(true);
  };

  const savePromoEdit = async () => {
    if (!promoDetailData?.promo_code) return;
    setPromoSavingEdit(true);
    try {
      const payload = { ...promoEditForm };
      payload.min_spend = payload.min_spend === '' ? null : parseFloat(payload.min_spend);
      payload.max_redemptions = payload.max_redemptions === '' ? null : parseInt(payload.max_redemptions, 10);
      payload.discount_percent = parseFloat(payload.discount_percent) || 0;
      payload.discount_amount = parseFloat(payload.discount_amount) || 0;
      const res = await axios.put(`${API}/promo-codes/${promoDetailData.promo_code.id}`, payload);
      if (res.data?.success) {
        toast.success('Promo code updated');
        const fresh = await axios.get(`${API}/promo-codes/${promoDetailData.promo_code.id}`);
        setPromoDetailData(fresh.data);
        setPromoEditMode(false);
        loadPromoCodes();
      } else {
        toast.error(res.data?.error || 'Failed to update');
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to update');
    }
    setPromoSavingEdit(false);
  };

  const cancelPromoCode = async (id) => {
    if (!window.confirm('Cancel this promo code? It can no longer be redeemed once cancelled.')) return;
    await axios.delete(`${API}/promo-codes/${id}`);
    toast.success('Promo code cancelled');
    loadPromoCodes();
    setPromoDetailDialog(false);
  };

  const filtered = vouchers.filter(v => {
    if (!search) return true;
    const s = search.toLowerCase();
    return v.code?.toLowerCase().includes(s) || v.issued_to_name?.toLowerCase().includes(s) ||
      v.recipient_name?.toLowerCase().includes(s) || v.issued_to_email?.toLowerCase().includes(s) ||
      v.issued_reason?.toLowerCase().includes(s);
  });

  const filteredPromos = promoCodes.filter(p => {
    if (promoFilterStatus !== 'all' && p.display_status !== promoFilterStatus) return false;
    if (!promoSearch) return true;
    const s = promoSearch.toLowerCase();
    return p.code?.toLowerCase().includes(s) || p.description?.toLowerCase().includes(s);
  });

  const statusColor = { ACTIVE: 'bg-green-100 text-green-700', PARTIALLY_REDEEMED: 'bg-blue-100 text-blue-700',
    REDEEMED: 'bg-gray-100 text-gray-500', EXPIRED: 'bg-yellow-100 text-yellow-700', CANCELLED: 'bg-red-100 text-red-700' };
  const typeColor = { GIFT_CARD: 'bg-pink-100 text-pink-700', PROMO: 'bg-purple-100 text-purple-700' };

  return (
    <div className="space-y-4 fade-in">
      <Toaster position="top-center" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Playfair Display' }}>
            <Gift size={24} className="text-primary" /> Vouchers &amp; Promo Codes
          </h1>
          <p className="text-sm text-muted-foreground">
            {tab === 'vouchers' ? `${vouchers.length} vouchers · ${stats.active_count || 0} active` : `${promoCodes.length} promo codes · ${promoStats.active_count || 0} active`}
          </p>
        </div>
        {tab === 'vouchers' ? (
          <Button data-testid="create-voucher-btn" onClick={() => { setForm({ type: 'GIFT_CARD', initial_value: 50, issued_to_name: '', issued_to_email: '', recipient_name: '', recipient_email: '', recipient_message: '', issued_reason: '', expiry_date: defaultExpiry(), min_spend: '' }); setDialog('new'); }} className="rounded-xl">
            <Plus size={16} className="mr-1" /> Create Voucher
          </Button>
        ) : (
          <Button data-testid="create-promo-code-btn" onClick={() => { setPromoForm(emptyPromoForm); setPromoDialog(true); }} className="rounded-xl">
            <Plus size={16} className="mr-1" /> Create Promo Code
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="vouchers-tabs">
          <TabsTrigger value="vouchers" data-testid="vouchers-tab-trigger"><Gift size={13} className="mr-1.5" /> Gift Cards &amp; Vouchers</TabsTrigger>
          <TabsTrigger value="promos" data-testid="promo-codes-tab-trigger"><Tag size={13} className="mr-1.5" /> Promo Codes</TabsTrigger>
        </TabsList>

        {/* ══════════════ GIFT CARDS / VOUCHERS TAB ══════════════ */}
        <TabsContent value="vouchers" className="space-y-4 mt-4">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-3 rounded-xl border text-center">
              <p className="text-xl font-semibold text-primary tabular-nums" style={{ fontFamily: 'Playfair Display' }}>£{(stats.vouchers_sold_month || 0).toFixed(0)}</p>
              <p className="text-[10px] text-muted-foreground">Sold This Month</p>
            </Card>
            <Card className="p-3 rounded-xl border text-center">
              <p className="text-xl font-semibold text-blue-500 tabular-nums" style={{ fontFamily: 'Playfair Display' }}>£{(stats.vouchers_redeemed_month || 0).toFixed(0)}</p>
              <p className="text-[10px] text-muted-foreground">Redeemed This Month</p>
            </Card>
            <Card className="p-3 rounded-xl border text-center">
              <p className="text-xl font-semibold text-orange-500 tabular-nums" style={{ fontFamily: 'Playfair Display' }}>£{(stats.outstanding_balance || 0).toFixed(0)}</p>
              <p className="text-[10px] text-muted-foreground">Outstanding Balance</p>
            </Card>
            <Card className="p-3 rounded-xl border text-center">
              <p className="text-xl font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{stats.active_count || 0}</p>
              <p className="text-[10px] text-muted-foreground">Active Vouchers</p>
            </Card>
          </div>

          {/* Search + Filter */}
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search code, name, email, reason" className="pl-9 rounded-xl" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-44 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {['ACTIVE','PARTIALLY_REDEEMED','REDEEMED','EXPIRED','CANCELLED'].map(s => <SelectItem key={s} value={s}>{s.replace('_',' ')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Voucher list */}
          <div className="space-y-2">
            {filtered.map(v => (
              <Card key={v.id} className="p-4 rounded-xl border shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => viewDetail(v)}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-white shrink-0">
                    <Gift size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-mono font-semibold text-sm tracking-wider">{v.code}</span>
                      <button onClick={e => { e.stopPropagation(); copyCode(v.code); }} className="text-muted-foreground hover:text-primary"><Copy size={12} /></button>
                      <Badge className={`text-[9px] rounded-full ${typeColor[v.type] || ''}`}>{v.type === 'GIFT_CARD' ? 'Gift Card' : 'Promo'}</Badge>
                      <Badge className={`text-[9px] rounded-full ${statusColor[v.status] || ''}`}>{v.status.replace('_', ' ')}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {v.recipient_name || v.issued_to_name || 'Unassigned'}
                      {v.issued_reason && ` · ${v.issued_reason}`}
                      {v.expiry_date && ` · Exp: ${v.expiry_date}`}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-lg font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }}>
                      <span className={v.remaining_value < v.initial_value ? 'text-orange-500' : 'text-primary'}>£{v.remaining_value?.toFixed(2)}</span>
                      {v.remaining_value < v.initial_value && <span className="text-xs text-muted-foreground ml-1">/ £{v.initial_value?.toFixed(0)}</span>}
                    </p>
                    <p className="text-[10px] text-muted-foreground">{safeFormat(v.created_at, 'd MMM yyyy')}</p>
                  </div>
                </div>
              </Card>
            ))}
            {filtered.length === 0 && (
              <Card className="p-12 rounded-xl text-center">
                <Gift size={40} className="mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">No vouchers found</p>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ══════════════ PROMO CODES TAB ══════════════ */}
        <TabsContent value="promos" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-3 rounded-xl border text-center">
              <p className="text-xl font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{promoStats.active_count || 0}</p>
              <p className="text-[10px] text-muted-foreground">Active Codes</p>
            </Card>
            <Card className="p-3 rounded-xl border text-center">
              <p className="text-xl font-semibold text-blue-500 tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{promoStats.redeemed_month || 0}</p>
              <p className="text-[10px] text-muted-foreground">Redemptions This Month</p>
            </Card>
            <Card className="p-3 rounded-xl border text-center">
              <p className="text-xl font-semibold text-orange-500 tabular-nums" style={{ fontFamily: 'Playfair Display' }}>£{(promoStats.discount_given_month || 0).toFixed(0)}</p>
              <p className="text-[10px] text-muted-foreground">Discount Given This Month</p>
            </Card>
            <Card className="p-3 rounded-xl border text-center">
              <p className="text-xl font-semibold text-primary tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{promoStats.total_codes || 0}</p>
              <p className="text-[10px] text-muted-foreground">Total Codes</p>
            </Card>
          </div>

          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={promoSearch} onChange={e => setPromoSearch(e.target.value)} placeholder="Search code or description" className="pl-9 rounded-xl" data-testid="promo-search-input" />
            </div>
            <Select value={promoFilterStatus} onValueChange={setPromoFilterStatus}>
              <SelectTrigger className="w-44 rounded-xl" data-testid="promo-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {['ACTIVE','SCHEDULED','EXPIRED','DEPLETED','CANCELLED'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            {filteredPromos.map(p => (
              <Card key={p.id} className="p-4 rounded-xl border shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => viewPromoDetail(p)} data-testid={`promo-card-${p.code}`}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-400 flex items-center justify-center text-white shrink-0">
                    <Tag size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-mono font-semibold text-sm tracking-wider">{p.code}</span>
                      <button onClick={e => { e.stopPropagation(); copyCode(p.code); }} className="text-muted-foreground hover:text-primary"><Copy size={12} /></button>
                      <Badge className={`text-[9px] rounded-full ${promoStatusColor[p.display_status] || ''}`}>{p.display_status}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {p.description || 'No description'}
                      {p.start_date || p.end_date ? ` · ${p.start_date || 'Any time'} → ${p.end_date || 'No end date'}` : ' · Always valid'}
                      {p.min_spend ? ` · Min £${Number(p.min_spend).toFixed(0)}` : ''}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-lg font-semibold tabular-nums text-primary" style={{ fontFamily: 'Playfair Display' }}>
                      {p.discount_type === 'percent' ? `${p.discount_percent}%` : `£${p.discount_amount?.toFixed(0)}`} off
                    </p>
                    <p className="text-[10px] text-muted-foreground">{p.redemption_count || 0} / {p.max_redemptions ?? '∞'} used</p>
                  </div>
                </div>
              </Card>
            ))}
            {filteredPromos.length === 0 && (
              <Card className="p-12 rounded-xl text-center">
                <Tag size={40} className="mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">No promo codes yet — create one like FESTIVE10 for 10% off</p>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Voucher Dialog */}
      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Playfair Display' }}><Gift size={18} className="inline mr-2 text-primary" />Create Voucher</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.type} onValueChange={v => setForm(p => ({...p, type: v}))}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GIFT_CARD">Gift Card</SelectItem>
                    <SelectItem value="PROMO">Promo Voucher</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Amount (£)</Label>
                <Input type="number" step="0.01" value={form.initial_value} onChange={e => setForm(p => ({...p, initial_value: parseFloat(e.target.value) || 0}))} className="mt-1 rounded-xl" />
                <div className="flex gap-1 mt-1">
                  {PRESET_AMOUNTS.map(a => (
                    <button key={a} onClick={() => setForm(p => ({...p, initial_value: a}))} className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${form.initial_value === a ? 'bg-primary text-white' : 'bg-muted hover:bg-primary/10'}`}>£{a}</button>
                  ))}
                </div>
              </div>
            </div>
            {form.type === 'GIFT_CARD' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Recipient Name</Label><Input value={form.recipient_name} onChange={e => setForm(p => ({...p, recipient_name: e.target.value}))} className="mt-1 rounded-xl" placeholder="Who is it for?" /></div>
                  <div><Label className="text-xs">Recipient Email</Label><Input value={form.recipient_email} onChange={e => setForm(p => ({...p, recipient_email: e.target.value}))} className="mt-1 rounded-xl" placeholder="Send voucher to..." /></div>
                </div>
                <div><Label className="text-xs">Gift Message (optional)</Label><Textarea value={form.recipient_message} onChange={e => setForm(p => ({...p, recipient_message: e.target.value}))} className="mt-1 rounded-xl" rows={2} placeholder="Happy birthday!" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Purchased By Name</Label><Input value={form.purchased_by_name} onChange={e => setForm(p => ({...p, purchased_by_name: e.target.value}))} className="mt-1 rounded-xl" /></div>
                  <div><Label className="text-xs">Purchased By Email</Label><Input value={form.purchased_by_email} onChange={e => setForm(p => ({...p, purchased_by_email: e.target.value}))} className="mt-1 rounded-xl" /></div>
                </div>
              </>
            )}
            {form.type === 'PROMO' && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Assign to Name</Label><Input value={form.issued_to_name} onChange={e => setForm(p => ({...p, issued_to_name: e.target.value}))} className="mt-1 rounded-xl" placeholder="Client name (optional)" /></div>
                <div><Label className="text-xs">Assign to Email</Label><Input value={form.issued_to_email} onChange={e => setForm(p => ({...p, issued_to_email: e.target.value}))} className="mt-1 rounded-xl" placeholder="Send to email" /></div>
              </div>
            )}
            <div><Label className="text-xs">Reason</Label><Input value={form.issued_reason} onChange={e => setForm(p => ({...p, issued_reason: e.target.value}))} className="mt-1 rounded-xl" placeholder="e.g. Referral reward, VIP thank you, Birthday" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Expiry Date <span className="text-muted-foreground">(3 months default)</span></Label>
                <Input type="date" value={form.expiry_date} onChange={e => setForm(p => ({...p, expiry_date: e.target.value}))} className="mt-1 rounded-xl" />
                <p className="text-[10px] text-muted-foreground mt-1">Shown on the voucher email to the client</p>
              </div>
              <div><Label className="text-xs">Min Spend £ (optional)</Label><Input type="number" value={form.min_spend} onChange={e => setForm(p => ({...p, min_spend: e.target.value}))} className="mt-1 rounded-xl" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={save} className="rounded-xl">Create & Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Voucher Detail Dialog */}
      <Dialog open={!!detailDialog} onOpenChange={() => { setDetailDialog(false); setEditMode(false); }}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto">
          {detailData && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Playfair Display' }} className="flex items-center gap-2">
                  <span className="font-mono tracking-wider">{detailData.voucher.code}</span>
                  {detailData.voucher.source === 'vip_reward' && (
                    <Badge className="rounded-full text-[9px] bg-amber-100 text-amber-700 border-amber-200">
                      <Sparkles size={9} className="mr-0.5" /> VIP Reward
                    </Badge>
                  )}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <Badge className={`rounded-full ${typeColor[detailData.voucher.type]}`}>{detailData.voucher.type === 'GIFT_CARD' ? 'Gift Card' : 'Promo'}</Badge>
                    <Badge className={`rounded-full ${statusColor[detailData.voucher.status]}`}>{detailData.voucher.status.replace('_', ' ')}</Badge>
                  </div>
                  <div className="flex gap-1">
                    {!editMode && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => copyCode(detailData.voucher.code)} className="rounded-lg h-7 text-xs" data-testid="voucher-copy-btn"><Copy size={12} className="mr-1" /> Copy</Button>
                        <Button variant="ghost" size="sm" onClick={() => resendEmail(detailData.voucher.id)} className="rounded-lg h-7 text-xs" data-testid="voucher-resend-btn"><Send size={12} className="mr-1" /> Resend</Button>
                        {detailData.voucher.status !== 'CANCELLED' && (
                          <Button variant="ghost" size="sm" onClick={startEdit} className="rounded-lg h-7 text-xs text-primary" data-testid="voucher-edit-btn"><Pencil size={12} className="mr-1" /> Edit</Button>
                        )}
                        {detailData.voucher.status !== 'CANCELLED' && (
                          <Button variant="ghost" size="sm" onClick={() => cancelVoucher(detailData.voucher.id)} className="rounded-lg h-7 text-xs text-destructive"><XCircle size={12} className="mr-1" /> Cancel</Button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {editMode ? (
                  // ── Edit form ──
                  <div className="space-y-3" data-testid="voucher-edit-form">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Amount (£)</Label>
                        <Input
                          type="number" step="0.01"
                          value={editForm.initial_value}
                          onChange={e => setEditForm(p => ({ ...p, initial_value: e.target.value }))}
                          className="mt-1 rounded-xl"
                          data-testid="edit-initial-value"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">
                          Redeemed so far: £{(((detailData.voucher.initial_value || 0) - (detailData.voucher.remaining_value || 0)) || 0).toFixed(2)} · stays unchanged
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs">Expiry Date</Label>
                        <Input
                          type="date"
                          value={editForm.expiry_date}
                          onChange={e => setEditForm(p => ({ ...p, expiry_date: e.target.value }))}
                          className="mt-1 rounded-xl"
                          data-testid="edit-expiry"
                        />
                      </div>
                    </div>
                    {detailData.voucher.discount_type === 'percent' && (
                      <div>
                        <Label className="text-xs">Discount %</Label>
                        <Input
                          type="number" min="0" max="100"
                          value={editForm.discount_percent}
                          onChange={e => setEditForm(p => ({ ...p, discount_percent: e.target.value }))}
                          className="mt-1 rounded-xl"
                          data-testid="edit-discount-percent"
                        />
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs">Recipient Name</Label><Input value={editForm.recipient_name} onChange={e => setEditForm(p => ({ ...p, recipient_name: e.target.value }))} className="mt-1 rounded-xl" data-testid="edit-recipient-name" /></div>
                      <div><Label className="text-xs">Recipient Email</Label><Input value={editForm.recipient_email} onChange={e => setEditForm(p => ({ ...p, recipient_email: e.target.value }))} className="mt-1 rounded-xl" data-testid="edit-recipient-email" /></div>
                    </div>
                    <div>
                      <Label className="text-xs">Gift Message</Label>
                      <Textarea value={editForm.recipient_message} onChange={e => setEditForm(p => ({ ...p, recipient_message: e.target.value }))} rows={2} className="mt-1 rounded-xl" data-testid="edit-message" />
                    </div>
                    {detailData.voucher.type === 'PROMO' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs">Issued To Name</Label><Input value={editForm.issued_to_name} onChange={e => setEditForm(p => ({ ...p, issued_to_name: e.target.value }))} className="mt-1 rounded-xl" /></div>
                        <div><Label className="text-xs">Issued To Email</Label><Input value={editForm.issued_to_email} onChange={e => setEditForm(p => ({ ...p, issued_to_email: e.target.value }))} className="mt-1 rounded-xl" /></div>
                      </div>
                    )}
                    {detailData.voucher.type === 'GIFT_CARD' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs">Purchased By Name</Label><Input value={editForm.purchased_by_name} onChange={e => setEditForm(p => ({ ...p, purchased_by_name: e.target.value }))} className="mt-1 rounded-xl" /></div>
                        <div><Label className="text-xs">Purchased By Email</Label><Input value={editForm.purchased_by_email} onChange={e => setEditForm(p => ({ ...p, purchased_by_email: e.target.value }))} className="mt-1 rounded-xl" /></div>
                      </div>
                    )}
                    <div>
                      <Label className="text-xs">Reason</Label>
                      <Input value={editForm.issued_reason} onChange={e => setEditForm(p => ({ ...p, issued_reason: e.target.value }))} className="mt-1 rounded-xl" data-testid="edit-reason" />
                    </div>
                    <div>
                      <Label className="text-xs">Min Spend £ (optional)</Label>
                      <Input type="number" value={editForm.min_spend} onChange={e => setEditForm(p => ({ ...p, min_spend: e.target.value }))} className="mt-1 rounded-xl" data-testid="edit-min-spend" />
                    </div>
                    <div className="flex justify-end gap-2 pt-2 border-t">
                      <Button variant="ghost" size="sm" onClick={() => setEditMode(false)} className="rounded-xl" data-testid="edit-cancel-btn">
                        <XIcon size={12} className="mr-1" /> Cancel
                      </Button>
                      <Button size="sm" onClick={saveEdit} disabled={savingEdit} className="rounded-xl" data-testid="edit-save-btn">
                        <SaveIcon size={12} className="mr-1" /> {savingEdit ? 'Saving…' : 'Save changes'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  // ── Read-only view ──
                  <>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="p-3 bg-muted/30 rounded-xl"><p className="text-[10px] text-muted-foreground">Initial Value</p><p className="font-semibold text-lg" style={{ fontFamily: 'Playfair Display' }}>£{detailData.voucher.initial_value?.toFixed(2)}</p></div>
                      <div className="p-3 bg-muted/30 rounded-xl"><p className="text-[10px] text-muted-foreground">Remaining</p><p className="font-semibold text-lg text-primary" style={{ fontFamily: 'Playfair Display' }}>£{detailData.voucher.remaining_value?.toFixed(2)}</p></div>
                    </div>
                    <div className="text-xs space-y-1">
                      {detailData.voucher.recipient_name && <p><span className="text-muted-foreground">Recipient:</span> {detailData.voucher.recipient_name}{detailData.voucher.recipient_email ? ` · ${detailData.voucher.recipient_email}` : ''}</p>}
                      {detailData.voucher.issued_to_name && <p><span className="text-muted-foreground">Issued to:</span> {detailData.voucher.issued_to_name}{detailData.voucher.issued_to_email ? ` · ${detailData.voucher.issued_to_email}` : ''}</p>}
                      {detailData.voucher.recipient_message && <p><span className="text-muted-foreground">Message:</span> "{detailData.voucher.recipient_message}"</p>}
                      {detailData.voucher.issued_reason && <p><span className="text-muted-foreground">Reason:</span> {detailData.voucher.issued_reason}</p>}
                      {detailData.voucher.expiry_date && <p><span className="text-muted-foreground">Expires:</span> {detailData.voucher.expiry_date}</p>}
                      {detailData.voucher.min_spend ? <p><span className="text-muted-foreground">Min spend:</span> £{Number(detailData.voucher.min_spend).toFixed(2)}</p> : null}
                      {detailData.voucher.discount_type === 'percent' && (
                        <p><span className="text-muted-foreground">Discount:</span> {detailData.voucher.discount_percent}%</p>
                      )}
                      <p><span className="text-muted-foreground">Created:</span> {safeFormat(detailData.voucher.created_at, 'd MMM yyyy HH:mm')}</p>
                    </div>
                  </>
                )}

                {/* Transaction history */}
                {!editMode && (
                  <div>
                    <h4 className="text-xs font-semibold mb-2">Transaction History</h4>
                    {(detailData.transactions || []).map(t => (
                      <div key={t.id} className="flex items-center justify-between py-1.5 border-b border-border/50 text-xs">
                        <div>
                          <Badge variant="outline" className="text-[9px] rounded-full mr-1">{t.type}</Badge>
                          <span className="text-muted-foreground">{t.notes}</span>
                        </div>
                        <div className="text-right">
                          <span className={`font-medium ${t.amount > 0 ? 'text-green-600' : t.amount < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                            {t.amount > 0 ? '+' : ''}{t.amount !== 0 ? `£${Math.abs(t.amount).toFixed(2)}` : '—'}
                          </span>
                          <p className="text-[9px] text-muted-foreground">{safeFormat(t.created_at, 'd MMM HH:mm')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Promo Code Dialog */}
      <Dialog open={promoDialog} onOpenChange={setPromoDialog}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-promo-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Playfair Display' }}><Tag size={18} className="inline mr-2 text-primary" />Create Promo Code</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Create a new promo code with a discount, validity window and optional usage limit.</DialogDescription>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Code</Label>
              <Input
                value={promoForm.code}
                onChange={e => setPromoForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                className="mt-1 rounded-xl font-mono tracking-wider"
                placeholder="e.g. FESTIVE10"
                data-testid="promo-code-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Customers enter this exact code at checkout</p>
            </div>
            <div>
              <Label className="text-xs">Description (internal note, optional)</Label>
              <Input value={promoForm.description} onChange={e => setPromoForm(p => ({ ...p, description: e.target.value }))} className="mt-1 rounded-xl" placeholder="e.g. Festive sale 2026" data-testid="promo-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Discount Type</Label>
                <Select value={promoForm.discount_type} onValueChange={v => setPromoForm(p => ({ ...p, discount_type: v }))}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="promo-discount-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentage off</SelectItem>
                    <SelectItem value="amount">Fixed amount off</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {promoForm.discount_type === 'percent' ? (
                <div>
                  <Label className="text-xs">Discount %</Label>
                  <Input type="number" min="1" max="100" value={promoForm.discount_percent} onChange={e => setPromoForm(p => ({ ...p, discount_percent: e.target.value }))} className="mt-1 rounded-xl" data-testid="promo-discount-percent-input" />
                </div>
              ) : (
                <div>
                  <Label className="text-xs">Discount £</Label>
                  <Input type="number" min="0.01" step="0.01" value={promoForm.discount_amount} onChange={e => setPromoForm(p => ({ ...p, discount_amount: e.target.value }))} className="mt-1 rounded-xl" data-testid="promo-discount-amount-input" />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Start Date (optional)</Label>
                <Input type="date" value={promoForm.start_date} onChange={e => setPromoForm(p => ({ ...p, start_date: e.target.value }))} className="mt-1 rounded-xl" data-testid="promo-start-date-input" />
                <p className="text-[10px] text-muted-foreground mt-1">Blank = active immediately</p>
              </div>
              <div>
                <Label className="text-xs">End Date (optional)</Label>
                <Input type="date" value={promoForm.end_date} onChange={e => setPromoForm(p => ({ ...p, end_date: e.target.value }))} className="mt-1 rounded-xl" data-testid="promo-end-date-input" />
                <p className="text-[10px] text-muted-foreground mt-1">Blank = never expires</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Min Spend £ (optional)</Label>
                <Input type="number" value={promoForm.min_spend} onChange={e => setPromoForm(p => ({ ...p, min_spend: e.target.value }))} className="mt-1 rounded-xl" data-testid="promo-min-spend-input" />
              </div>
              <div>
                <Label className="text-xs">Max Redemptions (optional)</Label>
                <Input type="number" min="1" value={promoForm.max_redemptions} onChange={e => setPromoForm(p => ({ ...p, max_redemptions: e.target.value }))} className="mt-1 rounded-xl" placeholder="Unlimited" data-testid="promo-max-redemptions-input" />
                <p className="text-[10px] text-muted-foreground mt-1">Blank = unlimited customers</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPromoDialog(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={savePromo} className="rounded-xl" data-testid="promo-save-btn">Create Promo Code</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promo Code Detail Dialog */}
      <Dialog open={promoDetailDialog} onOpenChange={() => { setPromoDetailDialog(false); setPromoEditMode(false); }}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="promo-detail-dialog">
          {promoDetailData && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Playfair Display' }} className="font-mono tracking-wider">{promoDetailData.promo_code.code}</DialogTitle>
              </DialogHeader>
              <DialogDescription className="sr-only">View, edit or cancel this promo code and see its redemption history.</DialogDescription>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Badge className={`rounded-full ${promoStatusColor[promoDetailData.promo_code.display_status] || ''}`}>{promoDetailData.promo_code.display_status}</Badge>
                  <div className="flex gap-1">
                    {!promoEditMode && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => copyCode(promoDetailData.promo_code.code)} className="rounded-lg h-7 text-xs" data-testid="promo-copy-btn"><Copy size={12} className="mr-1" /> Copy</Button>
                        {promoDetailData.promo_code.status !== 'CANCELLED' && (
                          <Button variant="ghost" size="sm" onClick={startPromoEdit} className="rounded-lg h-7 text-xs text-primary" data-testid="promo-edit-btn"><Pencil size={12} className="mr-1" /> Edit</Button>
                        )}
                        {promoDetailData.promo_code.status !== 'CANCELLED' && (
                          <Button variant="ghost" size="sm" onClick={() => cancelPromoCode(promoDetailData.promo_code.id)} className="rounded-lg h-7 text-xs text-destructive" data-testid="promo-cancel-btn"><XCircle size={12} className="mr-1" /> Cancel</Button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {promoEditMode ? (
                  <div className="space-y-3" data-testid="promo-edit-form">
                    <div>
                      <Label className="text-xs">Description</Label>
                      <Input value={promoEditForm.description} onChange={e => setPromoEditForm(p => ({ ...p, description: e.target.value }))} className="mt-1 rounded-xl" data-testid="promo-edit-description" />
                    </div>
                    {promoDetailData.promo_code.discount_type === 'percent' ? (
                      <div>
                        <Label className="text-xs">Discount %</Label>
                        <Input type="number" min="1" max="100" value={promoEditForm.discount_percent} onChange={e => setPromoEditForm(p => ({ ...p, discount_percent: e.target.value }))} className="mt-1 rounded-xl" data-testid="promo-edit-discount-percent" />
                      </div>
                    ) : (
                      <div>
                        <Label className="text-xs">Discount £</Label>
                        <Input type="number" min="0.01" step="0.01" value={promoEditForm.discount_amount} onChange={e => setPromoEditForm(p => ({ ...p, discount_amount: e.target.value }))} className="mt-1 rounded-xl" data-testid="promo-edit-discount-amount" />
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Start Date</Label>
                        <Input type="date" value={promoEditForm.start_date} onChange={e => setPromoEditForm(p => ({ ...p, start_date: e.target.value }))} className="mt-1 rounded-xl" data-testid="promo-edit-start-date" />
                      </div>
                      <div>
                        <Label className="text-xs">End Date</Label>
                        <Input type="date" value={promoEditForm.end_date} onChange={e => setPromoEditForm(p => ({ ...p, end_date: e.target.value }))} className="mt-1 rounded-xl" data-testid="promo-edit-end-date" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Min Spend £</Label>
                        <Input type="number" value={promoEditForm.min_spend} onChange={e => setPromoEditForm(p => ({ ...p, min_spend: e.target.value }))} className="mt-1 rounded-xl" data-testid="promo-edit-min-spend" />
                      </div>
                      <div>
                        <Label className="text-xs">Max Redemptions</Label>
                        <Input type="number" min="1" value={promoEditForm.max_redemptions} onChange={e => setPromoEditForm(p => ({ ...p, max_redemptions: e.target.value }))} className="mt-1 rounded-xl" placeholder="Unlimited" data-testid="promo-edit-max-redemptions" />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2 border-t">
                      <Button variant="ghost" size="sm" onClick={() => setPromoEditMode(false)} className="rounded-xl" data-testid="promo-edit-cancel-btn">
                        <XIcon size={12} className="mr-1" /> Cancel
                      </Button>
                      <Button size="sm" onClick={savePromoEdit} disabled={promoSavingEdit} className="rounded-xl" data-testid="promo-edit-save-btn">
                        <SaveIcon size={12} className="mr-1" /> {promoSavingEdit ? 'Saving…' : 'Save changes'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="p-3 bg-muted/30 rounded-xl">
                        <p className="text-[10px] text-muted-foreground">Discount</p>
                        <p className="font-semibold text-lg text-primary flex items-center gap-1" style={{ fontFamily: 'Playfair Display' }}>
                          <Percent size={14} />
                          {promoDetailData.promo_code.discount_type === 'percent' ? `${promoDetailData.promo_code.discount_percent}%` : `£${promoDetailData.promo_code.discount_amount?.toFixed(2)}`}
                        </p>
                      </div>
                      <div className="p-3 bg-muted/30 rounded-xl">
                        <p className="text-[10px] text-muted-foreground">Redemptions</p>
                        <p className="font-semibold text-lg" style={{ fontFamily: 'Playfair Display' }}>{promoDetailData.promo_code.redemption_count || 0} / {promoDetailData.promo_code.max_redemptions ?? '∞'}</p>
                      </div>
                    </div>
                    <div className="text-xs space-y-1">
                      {promoDetailData.promo_code.description && <p><span className="text-muted-foreground">Description:</span> {promoDetailData.promo_code.description}</p>}
                      <p><span className="text-muted-foreground">Valid:</span> {promoDetailData.promo_code.start_date || 'Any time'} → {promoDetailData.promo_code.end_date || 'No end date'}</p>
                      {promoDetailData.promo_code.min_spend ? <p><span className="text-muted-foreground">Min spend:</span> £{Number(promoDetailData.promo_code.min_spend).toFixed(2)}</p> : null}
                      <p><span className="text-muted-foreground">Created:</span> {safeFormat(promoDetailData.promo_code.created_at, 'd MMM yyyy HH:mm')}</p>
                    </div>
                  </>
                )}

                {!promoEditMode && (
                  <div>
                    <h4 className="text-xs font-semibold mb-2">Redemption History</h4>
                    {(promoDetailData.redemptions || []).map(r => (
                      <div key={r.id} className="flex items-center justify-between py-1.5 border-b border-border/50 text-xs">
                        <span className="text-muted-foreground">{r.client_name || 'Guest'}</span>
                        <div className="text-right">
                          <span className="font-medium text-green-600">-£{(r.amount_applied || 0).toFixed(2)}</span>
                          <p className="text-[9px] text-muted-foreground">{safeFormat(r.created_at, 'd MMM HH:mm')}</p>
                        </div>
                      </div>
                    ))}
                    {(!promoDetailData.redemptions || promoDetailData.redemptions.length === 0) && (
                      <p className="text-xs text-muted-foreground italic">No redemptions yet</p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
